import React, { useEffect, useState } from "react";
import parse from "html-react-parser";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Table from "react-bootstrap/Table";
import { CDN_INCLUDES, CDN_ASSETS } from "../../utils/cdn";
import fetchJsonWithFallback from "../../utils/fetchJsonWithFallback";
import localPrivacyPolicy from "../../data/privacyPolicy.json";
import "./index.css";

// Token in the JSON content that gets swapped for the real, encoded CDN asset URL
// at render time (keeps the JSON free of build-time env interpolation).
const FORM_PDF_TOKEN = "{{FORM_PDF_URL}}";
const FORM_PDF_URL =
  CDN_ASSETS + encodeURIComponent("Non-State - English Form.pdf");

function renderHtml(text = "") {
  return parse(text.split(FORM_PDF_TOKEN).join(FORM_PDF_URL));
}

function renderContentBlock(block, key) {
  switch (block.type) {
    case "paragraph":
      return (
        <div
          className={"privacy-section__body" + (block.spaced ? " mb-2" : "")}
          key={key}
        >
          {renderHtml(block.text)}
        </div>
      );

    case "list":
      return (
        <ul className="privacy-list" key={key}>
          {block.items.map((item, i) => (
            <li key={i}>{renderHtml(item)}</li>
          ))}
        </ul>
      );

    case "table":
      return (
        <>
          {/* Desktop table */}
          <div className="privacy-table-wrap privacy-table-desktop" key={key}>
            <Table responsive className="privacy-table">
              <thead>
                <tr>
                  {block.columns.map((col, i) => (
                    <th scope="col" key={i}>{renderHtml(col)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, i) => (
                  <tr key={i}>
                    <th scope="row">{renderHtml(row[0])}</th>
                    {row.slice(1).map((cell, j) => (
                      <td key={j}>{renderHtml(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="privacy-table-mobile" key={key + "-mobile"}>
            <p className="privacy-table-mobile__heading">{renderHtml(block.columns[0])}</p>
            {block.rows.map((row, i) => {
              const html = row[0];
              const dashIdx = html.indexOf(" -");
              const brIdx = html.indexOf("<br");
              const splitAt = dashIdx !== -1 ? dashIdx : brIdx !== -1 ? brIdx : -1;
              const titleHtml = splitAt !== -1 ? html.slice(0, splitAt) : html;
              const descHtml = splitAt !== -1 ? html.slice(splitAt).replace(/^\s*-\s*/, "").replace(/^<br\s*\/?>/i, "") : "";
              return (
                <div className="privacy-card-row" key={i}>
                  <div className="privacy-card-row__title">{renderHtml(titleHtml)}</div>
                  {descHtml && <div className="privacy-card-row__desc">{renderHtml(descHtml)}</div>}
                  <div className="privacy-card-row__answers">
                    <div className="privacy-card-row__answer">
                      <span className="privacy-card-row__label">{renderHtml(block.columns[1])}</span>
                      <strong>{renderHtml(row[1])}</strong>
                    </div>
                    <div className="privacy-card-row__answer">
                      <span className="privacy-card-row__label">{renderHtml(block.columns[2])}</span>
                      <strong>{renderHtml(row[2])}</strong>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      );

    default:
      return null;
  }
}

function renderSectionGroup(group, key, id) {
  return (
    <div className="privacy-section" id={id} key={key}>
      {group.pairs.map((pair, i) => (
        <React.Fragment key={i}>
          <h3
            className={
              "privacy-section__heading" +
              (pair.tight ? " privacy-section__heading--tight" : "")
            }
          >
            {renderHtml(pair.heading)}
          </h3>
          <div className="privacy-section__body">
            {pair.body.map((block, j) =>
              renderContentBlock(block, `${key}-${i}-${j}`)
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function renderSection(section) {
  let idAssigned = false;

  return (
    <React.Fragment key={section.id}>
      {section.eyebrow ? (
        <div className="privacy-section" id={section.id}>
          <h3 className="privacy-section__heading">
            {renderHtml(section.eyebrow)}
          </h3>
          <div className="privacy-section__body">
            {renderHtml(section.heading)}
          </div>
        </div>
      ) : (
        <div className="privacy-divider">
          <div className="privacy-divider__rule"></div>
          <h1 className="privacy-divider__title">
            {renderHtml(section.heading)}
          </h1>
        </div>
      )}

      {section.blocks.map((block, i) => {
        if (block.type === "table") {
          return renderContentBlock(block, `${section.id}-${i}`);
        }

        const isFirstGroup = !idAssigned && !section.eyebrow;
        if (isFirstGroup) {
          idAssigned = true;
        }

        return renderSectionGroup(
          block,
          `${section.id}-${i}`,
          isFirstGroup ? section.id : undefined
        );
      })}
    </React.Fragment>
  );
}

export default function PrivacyPolicy() {
  const [policy, setPolicy] = useState(localPrivacyPolicy);

  useEffect(() => {
    let isMounted = true;

    fetchJsonWithFallback(
      CDN_INCLUDES + "privacyPolicy.json",
      localPrivacyPolicy
    ).then((data) => {
      if (isMounted) {
        setPolicy(data);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Container className="privacy-page">
      <Row>
        <Col xs={12} className="privacy-card animate__animated animate__fadeIn">
          <div className="privacy-revision">{parse(policy.revision)}</div>
          {policy.sections.map((section) => renderSection(section))}
        </Col>
      </Row>
    </Container>
  );
}
